/**
 * The panel beside the chat (GyroSidePanel.tsx): one container with a slot in the workspace row, a tab bar, a header (the
 * active tab's own controls, then Full screen and Close), a splitter and the active tab's body. The container owns being
 * open or closed, its width and splitter, Full screen and Esc, and the tab bar, which shows only when two or more tabs are
 * registered (with Preview alone it looks exactly as drawn, owner 2026-10-22). A feature adds a tab through
 * `useSidePanel().register(tab)` and opens it with `open(tabId, payload)`; the web page preview (htmlPreview.ts,
 * useHtmlPreview.ts, HtmlPreviewTab.tsx) is the first tab and registers itself the same way (owner, 2026-10-22: the panel
 * switches with tabs "Preview | StarScribe").
 *
 * Types, the context and the hooks only: no component, so the core holds the panel's state without loading the panel
 * itself (the web entry loads it on demand, webLater.ts).
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
const inert = { register: () => () => undefined, open: () => undefined, close: () => undefined, activeTabId: null, payloadFor: () => undefined };
/** Provided by the widget's core around everything it renders. */
export const SidePanelContext = createContext(inert);
/** The side panel of the Gyro panel this component renders in (an inert one outside a Gyro panel). */
export function useSidePanel() {
    return useContext(SidePanelContext);
}
/** The side panel's state, owned by the core (GyroAssistant.tsx), which provides `api` to everything it renders. */
export function useSidePanelHost() {
    const [tabs, setTabs] = useState([]);
    const [activeTabId, setActiveTabId] = useState(null);
    const payloads = useRef(new Map());
    const [, setPayloadRevision] = useState(0);
    const [fullScreenState, setFullScreenState] = useState(false);
    const [fraction, setFractionState] = useState(SIDE_PANEL_DEFAULT_FRACTION);
    const register = useCallback((tab) => {
        setTabs((current) => (current.some((existing) => existing.id === tab.id) ? current.map((existing) => (existing.id === tab.id ? tab : existing)) : [...current, tab]));
        return () => setTabs((current) => current.filter((existing) => existing !== tab));
    }, []);
    const open = useCallback((tabId, payload) => {
        if (payload !== undefined) {
            payloads.current.set(tabId, payload);
            setPayloadRevision((value) => value + 1);
        }
        setActiveTabId(tabId);
    }, []);
    const close = useCallback(() => {
        setActiveTabId(null);
        setFullScreenState(false);
    }, []);
    const payloadFor = useCallback((tabId) => payloads.current.get(tabId), []);
    const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;
    const api = useMemo(() => ({ register, open, close, activeTabId: activeTab ? activeTab.id : null, payloadFor }), [register, open, close, activeTab, payloadFor]);
    const setFraction = useCallback((value) => setFractionState(clampSidePanelFraction(value)), []);
    return { api, tabs, activeTab, fullScreen: activeTab !== null && fullScreenState, setFullScreen: setFullScreenState, fraction, setFraction };
}
/** The web page preview's tab id. */
export const PREVIEW_TAB_ID = 'preview';
/** The panel takes this share of the workspace row at first ("about 40% of the window", as drawn). */
export const SIDE_PANEL_DEFAULT_FRACTION = 0.4;
/** The splitter's range. The stylesheet keeps the panel at least 300 px wide and the chat at least 340 px. */
export const SIDE_PANEL_MIN_FRACTION = 0.25;
export const SIDE_PANEL_MAX_FRACTION = 0.7;
/** One arrow key press of the splitter. */
export const SIDE_PANEL_FRACTION_STEP = 0.05;
/** A share of the row the splitter may set: within its range, at two decimals; anything that is not a number is the default. */
export function clampSidePanelFraction(value) {
    if (!Number.isFinite(value))
        return SIDE_PANEL_DEFAULT_FRACTION;
    return Math.round(Math.min(SIDE_PANEL_MAX_FRACTION, Math.max(SIDE_PANEL_MIN_FRACTION, value)) * 100) / 100;
}
/**
 * The narrowest Gyro window or panel that shows the side panel: the desktop app's expanded window (1080 px) and the web
 * panel expanded on a wide page (960 px) qualify; the desktop's narrow window (560 px) and the 400 px web panel do not
 * (owner, 2026-10-22: "not narrow or compact layouts").
 */
export const SIDE_PANEL_MIN_SURFACE_WIDTH = 900;
/**
 * The desktop app makes room for the side panel (the Primary, 2026-10-22, for every tab): when the panel opens in a window
 * narrower than the chat's minimum plus the panel's minimum (plus the docked navigation), the page asks the native host to
 * widen the window (`gyro:side-panel-widen`, which the shell relays as `{"type":"panel:widen","minWidthDip":N}`), and when it
 * closes, to put the window back (`gyro:side-panel-restore`, relayed as `{"type":"panel:restore"}`). The host keeps the
 * window on its monitor's work area and restores it only if the person did not move or resize it meanwhile. Widths in DIP
 * (CSS pixels in the desktop's WebView). The web widget lays out in the space it has and asks nothing.
 */
export const SIDE_PANEL_CHAT_MIN_DIP = 520;
export const SIDE_PANEL_PANEL_MIN_DIP = 480;
/** The docked navigation column (236 px and its 12 px margin, gyro.css .gyro-desktop-sidebar). */
export const SIDE_PANEL_SIDEBAR_DIP = 248;
export const SIDE_PANEL_WIDEN_EVENT = 'gyro:side-panel-widen';
export const SIDE_PANEL_RESTORE_EVENT = 'gyro:side-panel-restore';
/** The window width the open panel needs on the desktop: the chat's and the panel's minimums, and the docked navigation. */
export function sidePanelWindowWidth(options) {
    return SIDE_PANEL_CHAT_MIN_DIP + SIDE_PANEL_PANEL_MIN_DIP + (options.sidebarDocked ? SIDE_PANEL_SIDEBAR_DIP : 0);
}
/**
 * Whether a surface can ever show the side panel: the workspace look outside a pane, which is the desktop app and the
 * floating web panel in a host app. Not a pane (the Office pane, Gyro on the web), not the classic compact widget.
 */
export function sidePanelSurface(options) {
    return options.workspace && options.layout !== 'pane';
}
//# sourceMappingURL=sidePanel.js.map